import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useFlag from 'hooks/useFlag'
import type { OrderingProvider } from 'models/Merchant'
import { useAbility } from 'utilities/ability'

// Ordering providers whose menu / POS-configuration errors are surfaced in the
// Operations Center "POS Configurations" page. Toast today; add a provider here
// (the single source of truth) once its menu-errors feed is built end-to-end
// (BE get_merchant_menu_errors + the FE provider card). DATA-5395.
const MENU_ERRORS_PROVIDERS: OrderingProvider[] = ['toast']

/**
 * Determines whether the merchant + current user can access ordering-related
 * nav items. Used by Operations Center, Digital Experiences, Settings, and
 * report views to gate ordering management, ordering error report, and
 * ordering configuration links consistently.
 *
 * Three orthogonal primitives — consumers compose what they need:
 *
 * - `supportsOrderingProvider` — capability only: provider supports integrated
 *   ordering. Use for visibility gates that don't need RBAC (e.g. report views,
 *   Settings tabs that handle their own RBAC).
 * - `supportsOrderingManagement` — capability only: provider supports Ordering
 *   Management. Strict subset of `supportsOrderingProvider`. Use for visibility
 *   of OM nav items.
 * - `supportsMenuErrors` — capability only: the provider's menu / POS-config
 *   errors are surfaced in the OC "POS Configurations" page. Allowlist-based
 *   (`MENU_ERRORS_PROVIDERS`, Toast today). Use to gate the POS Configurations
 *   nav item + page.
 * - `orderingEnabled` — fully gated: capability + `merchant.ordering_enabled`
 *   + `manage OrderingManagement` RBAC. Use for the base ordering feature's
 *   "is this clickable" gate.
 *
 * To compose "OM is clickable for this user", AND `orderingEnabled` with
 * `supportsOrderingManagement` at the call site.
 */
export const useOrderingAccess = () => {
  const merchant = useCurrentMerchant()
  const ability = useAbility()
  const canUseCapabilityGate = useFlag(
    'use-capability-ordering-settings-mx',
    false
  )

  const hasIntegratedOrderingCapability = Boolean(
    merchant?.supports_integrated_ordering
  )
  const hasLegacyOrderingProvider =
    merchant?.ordering_provider === 'toast' ||
    merchant?.ordering_provider === 'olo' ||
    merchant?.ordering_provider === 'square'

  const supportsOrderingProvider = canUseCapabilityGate
    ? hasIntegratedOrderingCapability
    : hasLegacyOrderingProvider

  // Subset invariant: ordering management requires integrated ordering. The
  // AND-gate prevents an inconsistent backend payload from rendering OM nav
  // for a provider that doesn't support integrated ordering.
  const hasOrderingManagementCapability = Boolean(
    merchant?.supports_integrated_ordering &&
      merchant?.supports_ordering_management
  )
  const hasLegacyOrderingManagementProvider =
    merchant?.ordering_provider === 'toast'

  const supportsOrderingManagement = canUseCapabilityGate
    ? hasOrderingManagementCapability
    : hasLegacyOrderingManagementProvider
  const orderingEnabled = Boolean(
    merchant?.ordering_enabled &&
      ability.can('manage', 'OrderingManagement') &&
      supportsOrderingProvider
  )

  // Allowlist by provider — independent of the capability-gate flag above,
  // since there's no `supports_menu_errors` backend capability field. When one
  // lands, mirror the `canUseCapabilityGate` pattern used by the others.
  const supportsMenuErrors = merchant?.ordering_provider
    ? MENU_ERRORS_PROVIDERS.includes(merchant.ordering_provider)
    : false

  return {
    orderingEnabled,
    supportsOrderingProvider,
    supportsOrderingManagement,
    supportsMenuErrors,
  }
}

export default useOrderingAccess
